import { DateInput, Select, TextInput } from '@octano/global-ui';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Col, FormGroup, Input, Row } from 'reactstrap';
import SectionTitle from '../../../../../../../components/text/SectionTitle';
import { useValidations } from '../../../../../../../hooks/useValidations';
import { calculateAge } from '../../../../../../../utils/dates';
import { formatOptions } from '../../../../../../../utils/selectFormat';
import { useAsyncOptionsFieldsLoader } from '../../../../../../StudentFileAsyncOptions/AsyncOptionsFieldsLoader';
import StudentPhoto, {
  StudentPhotoTexts,
} from '../../../../../../TuitionProcess/parts/StudentFile/StudentPhoto';

type StudentBackgroundNoSuaReformadaProps = {
  postulantId?: string | number;
  photo: string | null;
  isStudentFileUpdate?: boolean;
  optionalFields?: string[];
  fieldNames?: {
    schoolId: string;
    isLegalAge: string;
    identificationType: string;
    identification: string;
    documentIssueDate: string;
    documentIssuePlace: string;
    passportCountry: string;
    namesStudent: string;
    paternalSurname: string;
    maternalSurname: string;
    birthday: string;
    nationality: string;
    birdCountry: string;
    birdMunicipality: string;
    sexID: string;
    maritalStatus: string;
    personalEmail: string;
    officialEmail: string;
  };
  disabled?: boolean;
  hasEmail?: boolean;
  readOnly?: boolean;
};

/**
 * Sección de Antecedentes del estudiante SIN SUA
 */

const StudentBackgroundNoSuaReformada = ({
  postulantId,
  photo,
  isStudentFileUpdate = false,
  readOnly = false,
  optionalFields = [],
  fieldNames = {
    schoolId: 'personalInformation_schoolId',
    isLegalAge: 'personalInformation_isLegalAge',
    identificationType: 'personalInformation_identificationType',
    identification: 'personalInformation_identification',
    documentIssueDate: 'personalInformation_documentIssueDate',
    documentIssuePlace: 'personalInformation_documentIssuePlace',
    passportCountry: 'personalInformation_passportCountry',
    nationality: 'personalInformation_nationality',
    namesStudent: 'personalInformation_name',
    paternalSurname: 'personalInformation_firstLastname',
    maternalSurname: 'personalInformation_secondLastName',
    birthday: 'personalInformation_birthdate',
    birdCountry: 'personalInformation_countryOfBirth',
    birdMunicipality: 'personalInformation_municipalityOfBirth',
    sexID: 'personalInformation_gender',
    maritalStatus: 'personalInformation_maritalStatus',
    personalEmail: 'personalInformation_personalEmail',
    officialEmail: 'personalInformation_contactEmail',
  },
}: StudentBackgroundNoSuaReformadaProps) => {
  const { control, watch, formState, setValue } = useFormContext();
  const { t } = useTranslation();
  const { validateTextNotEmpty } = useValidations();
  const {
    data: { tenanCountry, countries, communes, genders, maritalStatus } = {
      data: [],
    },
  } = useAsyncOptionsFieldsLoader();

  const prefix = `tuitionProcess.onSite.studentFile.backgroundSectionReformada`;
  const studentPhotoTexts = t<any, StudentPhotoTexts>(
    `tuitionProcess.onSite.studentFile.studentPhoto`,
    { returnObjects: true },
  );

  const [birthday, officialEmail, identificationTypeId] = watch([
    fieldNames.birthday,
    fieldNames.officialEmail,
    fieldNames.identificationType,
  ]);

  const handleCalculate = useMemo(() => {
    if (birthday) {
      return calculateAge(birthday);
    }
    return null;
  }, [birthday]);

  // Handle Regions
  const [selectedCountry] = watch([fieldNames.birdCountry]);
  const isCountryTouched = formState.touchedFields[fieldNames.birdCountry];

  // Effects
  useEffect(() => {
    if (isCountryTouched) {
      setValue(fieldNames.birdMunicipality, null);
    }
  }, [setValue, isCountryTouched, fieldNames.birdMunicipality]);

  useEffect(() => {
    if (!handleCalculate) {
      return;
    }
    setValue(fieldNames.isLegalAge, handleCalculate > 18 ? true : false);
  }, [handleCalculate, fieldNames.isLegalAge, setValue]);

  const optional = (value: string, fieldName: string) => {
    if (optionalFields.includes(fieldName)) {
      return undefined;
    }
    return validateTextNotEmpty(value);
  };

  // Memo
  const isMunicipalityEnabled = useMemo(() => {
    return selectedCountry?.value === tenanCountry?.id;
  }, [selectedCountry, tenanCountry]);

  const countryOptions = useMemo(() => {
    return formatOptions(countries ?? []);
  }, [countries]);

  const communeOptions = useMemo(() => {
    return formatOptions(communes ?? []);
  }, [communes]);

  const genderOptions = useMemo(() => {
    return formatOptions(genders ?? []);
  }, [genders]);

  const maritalStatusOptions = useMemo(() => {
    return formatOptions(maritalStatus ?? []);
  }, [maritalStatus]);

  return (
    <>
      <Row>
        <Col xs={12}>
          <SectionTitle text={t(`${prefix}.title`)} />
        </Col>
      </Row>
      <Row>
        <Col className="pb-5" xs={12} lg="auto">
          <FormGroup>
            <div className="m-auto mw-100" style={{ width: '121px' }}>
              <StudentPhoto
                postulantId={postulantId}
                texts={studentPhotoTexts}
                defaultPhoto={photo}
                isStudentFileUpdate={isStudentFileUpdate}
                disabledUpdate={readOnly}
              />
            </div>
          </FormGroup>
        </Col>
        <Col xs={12} lg={true}>
          <Row>
            <Col className="pb-3" xs={12} lg={6}>
              <Input
                type="hidden"
                name={`${fieldNames.schoolId}`}
                label={t(`${prefix}.${fieldNames.schoolId}`)}
                control={control}
                readOnly
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <Input
                type="hidden"
                name={`${fieldNames.isLegalAge}`}
                label={t(`${prefix}.${fieldNames.isLegalAge}`)}
                control={control}
                readOnly
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.identificationType}`}
                label={t(`${prefix}.${fieldNames.identificationType}`)}
                rules={{ validate: validateTextNotEmpty }}
                control={control}
                shouldUnregister={true}
                readOnly
              />
            </Col>
            {identificationTypeId === 'PASSPORT' && (
              <Col xs={5} className="pr-1 pr-md-2">
                <Select
                  name={`${fieldNames.passportCountry}`}
                  label={t(`${prefix}.${fieldNames.passportCountry}`)}
                  options={countryOptions}
                  isClearable={false}
                  control={control}
                  shouldUnregister={true}
                  disabled
                />
              </Col>
            )}
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.identification}`}
                label={t(`${prefix}.${fieldNames.identification}`)}
                rules={{ validate: validateTextNotEmpty }}
                control={control}
                shouldUnregister={true}
                readOnly
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.documentIssuePlace}`}
                label={t(`${prefix}.${fieldNames.documentIssuePlace}`)}
                rules={{ validate: validateTextNotEmpty }}
                control={control}
                readOnly
                shouldUnregister={true}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <DateInput
                name={fieldNames.documentIssueDate}
                label={t(`${prefix}.${fieldNames.documentIssueDate}`)}
                control={control}
                disabled
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.namesStudent}`}
                label={t(`${prefix}.${fieldNames.namesStudent}`)}
                control={control}
                disabled={!isStudentFileUpdate}
                readOnly={readOnly}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={6}>
              <TextInput
                name={`${fieldNames.paternalSurname}`}
                label={t(`${prefix}.${fieldNames.paternalSurname}`)}
                control={control}
                disabled={!isStudentFileUpdate}
                readOnly={readOnly}
              />
            </Col>
          </Row>
        </Col>
      </Row>
      <Row>
        <Col className="pb-3" xs={12} lg={4}>
          <TextInput
            name={`${fieldNames.maternalSurname}`}
            label={t(`${prefix}.${fieldNames.maternalSurname}`)}
            control={control}
            disabled={!isStudentFileUpdate}
            readOnly={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.nationality}`}
            label={t(`${prefix}.${fieldNames.nationality}`)}
            options={countryOptions}
            control={control}
            isSearchable={true}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={fieldNames.birdCountry}
            label={t(`${prefix}.${fieldNames.birdCountry}`)}
            options={countryOptions}
            control={control}
            rules={{
              validate: {
                require: (value) => {
                  return optional(value, fieldNames.birdCountry);
                },
              },
            }}
          />
        </Col>
        {isMunicipalityEnabled ? (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <Select
                name={fieldNames.birdMunicipality}
                label={t(`${prefix}.${fieldNames.birdMunicipality}`)}
                options={communeOptions}
                control={control}
                rules={{
                  validate: {
                    require: (value) =>
                      optional(value, fieldNames.birdMunicipality),
                  },
                }}
              />
            </Col>
          </>
        ) : (
          <></>
        )}
        <Col className="pb-3" xs={12} lg={4}>
          <DateInput
            name={`${fieldNames.birthday}`}
            label={t(`${prefix}.${fieldNames.birthday}`)}
            control={control}
            disabled
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.sexID}`}
            label={t(`${prefix}.${fieldNames.sexID}`)}
            options={genderOptions}
            isSearchable={false}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        <Col className="pb-3" xs={12} lg={4}>
          <Select
            name={`${fieldNames.maritalStatus}`}
            label={t(`${prefix}.${fieldNames.maritalStatus}`)}
            options={maritalStatusOptions}
            isSearchable={false}
            control={control}
            rules={{ validate: validateTextNotEmpty }}
            disabled={readOnly}
          />
        </Col>
        {isStudentFileUpdate && (
          <>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={`${fieldNames.personalEmail}`}
                label={t(`${prefix}.${fieldNames.personalEmail}`)}
                control={isStudentFileUpdate ? control : undefined}
                rules={{ validate: validateTextNotEmpty }}
                readOnly={readOnly}
              />
            </Col>
            <Col className="pb-3" xs={12} lg={4}>
              <TextInput
                name={`${fieldNames.officialEmail}`}
                label={t(`${prefix}.${fieldNames.officialEmail}`)}
                control={isStudentFileUpdate ? control : undefined}
                disabled={isStudentFileUpdate ? !officialEmail : true}
                tooltip={t(`${prefix}.tooltip.email`)}
                readOnly={readOnly}
              />
            </Col>
          </>
        )}
      </Row>
    </>
  );
};

export default StudentBackgroundNoSuaReformada;
